































import {Component, Prop, Vue} from 'vue-property-decorator';
import {Inject} from "vue-typedi";
import OrderPanel from "@/components/orders/OrderPanel.vue";
import ProductPanel from "@/components/products/ProductPanel.vue";
import ItemsListDashboard, { DisplayMode } from "@/components/ItemsListDashboard.vue";
import { TabType } from '@/model/TabType';
import { EventBus } from '@/helpers/EventBus';
import {Order} from '@/model/Order';
import {OrderedProduct} from '@/model/OrderedProduct';
import { OrdersList } from '@/model/OrdersList';
import { OrderedProductsList } from '@/model/OrderedProductsList';
import { Product } from '@/model/product/Product';
import { ProductService } from '@/services/ProductService';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { UserService } from '@/services/UserService';
dayjs.extend(isSameOrAfter)


@Component({
  components: {ItemsListDashboard, OrderPanel, ProductPanel}
})
export default class TabBody extends Vue {
   
    @Prop({default: TabType.TO_PROCESS}) public type: TabType;
    @Prop({default: DisplayMode.ORDERS}) public displayMode: DisplayMode;
    @Prop({default: false}) public canHandleSupports: boolean;
    @Prop({default: 0}) public resetMissingFilterKey: number;

    @Inject()
    productService!: ProductService;

    @Inject()
    userService!: UserService;

    private ordersList: OrdersList = null;
    private orderedProductsList: OrderedProductsList = null;
    private openedCards: string[] = [];

    created() {
        EventBus.$on(EventBus.REFRESH_ORDERS_LIST, this.onRefreshOrdersList);
        EventBus.$on(EventBus.REFRESH_ORDERED_PRODUCTS_LIST, this.onRefreshOrderedProductsList);
        EventBus.$on(EventBus.TOGGLE_FOLDING, this.onToggleFolding);
    }

    beforeDestroy() {
        EventBus.$off(EventBus.REFRESH_ORDERS_LIST, this.onRefreshOrdersList);
        EventBus.$off(EventBus.REFRESH_ORDERED_PRODUCTS_LIST, this.onRefreshOrderedProductsList);
        EventBus.$off(EventBus.TOGGLE_FOLDING, this.onToggleFolding);
    }

    private refreshProductStocks(orderedProductsList: OrderedProductsList) {
        this.userService.getUserRights()
            .then(userRights => userRights.zonesAccreditations.map(zone => zone.zone))
            .then(zones => this.productService.getProductsByZones(zones))
            .then(products => this.populateOrderedProducts(orderedProductsList, products))
            .catch(() => this.orderedProductsList = orderedProductsList);
    }

    private populateOrderedProducts(orderedProductsList: OrderedProductsList, referenceProducts: Product[]) {
        const allOrderedProducts = [...orderedProductsList.productsProcessed, ...orderedProductsList.productsToProcess];

        allOrderedProducts.forEach((orderedProduct: OrderedProduct) => {
            const matchingProduct: Product = referenceProducts.find(referenceProduct => referenceProduct.cug === orderedProduct.product.cug);
            if (!matchingProduct) {
                return;
            }

            this.populateProductZoneCode(orderedProduct, matchingProduct);
            this.populateProductOutOfStockStatus(orderedProduct, matchingProduct);
        });

        this.orderedProductsList = orderedProductsList;
    }

    private populateProductZoneCode(orderedProduct: OrderedProduct, matchingProduct: Product) {
        orderedProduct.product.zoneCode = matchingProduct.zoneCode;
    }

    private populateProductOutOfStockStatus(orderedProduct: OrderedProduct, matchingProduct: Product) {
        const lastOutOfStockDate = matchingProduct.lastOutOfStockDate;
        if (!lastOutOfStockDate) {
            orderedProduct.product.inStockToday = true;
            return;
        }
        const lastOutOfStockDateAsStr = dayjs(lastOutOfStockDate).format('YYYY-MM-DD');
        const todayAsStr = dayjs().format('YYYY-MM-DD');
        orderedProduct.product.inStockToday = lastOutOfStockDateAsStr !== todayAsStr;
    }

    public toggleAllCardsFolding(open: boolean) {
        if (open) {
            this.openedCards = [...this.orders.map(order => order.orderId), ...this.products.map(orderedProduct => orderedProduct.product.cug)];
        } else {
            this.openedCards = [];
        }
    }

    public toggleCardFolding(cardId: string) {
        if (this.isCardOpened(cardId)) {
            this.openedCards = this.openedCards.filter(id => id !== cardId);
        } else {
            this.openedCards.push(cardId);
        }
    }

    public isCardOpened(cardId: string): boolean {
        return this.openedCards.includes(cardId);
    }

    private onRefreshOrdersList(data: OrdersList) {
        this.ordersList = data;
    }

    private onRefreshOrderedProductsList(data: OrderedProductsList) {
        this.refreshProductStocks(data);
    }

    private onToggleFolding(open: boolean) {
        this.toggleAllCardsFolding(open);
    }

    get isAllUnfolded(): boolean {
        if (this.isDisplayedByOrders) {
            return this.orders.find(order => !this.isCardOpened(order.orderId)) == null;
        } else {
            return this.products.find(orderedProduct => !this.isCardOpened(orderedProduct.product.cug)) == null;
        }
    }

    get todayOrders(): Order[] {
        const nextDay = dayjs().add(1, 'day').startOf('day');
        return this.orders.filter(order => dayjs(order.dueDate).isBefore(nextDay))
            .sort(this.displayedOrdersSort);
    }

    get tomorrowOrders(): Order[] {
        const nextDay = dayjs().add(1, 'day').startOf('day');
        return this.orders.filter(order => dayjs(order.dueDate).isSameOrAfter(nextDay))
            .sort(this.displayedOrdersSort);
    }

    get archivedOrders(): Order[] {
        if (this.displayMode !== DisplayMode.ORDERS || !this.ordersList) {
            return [];
        }
        
        return this.ordersList.ordersArchived
            .sort(this.displayedOrdersSort);
    }

    get orders(): Order[] {
        if (this.displayMode !== DisplayMode.ORDERS || !this.ordersList) {
            return [];
        }

        return this.type === TabType.TO_PROCESS ?
            this.ordersList.ordersToProcess.sort(this.displayedOrdersSort) :
            this.ordersList.ordersProcessed.sort(this.displayedOrdersSort);
    }

    get products(): OrderedProduct[] {
        if (this.displayMode !== DisplayMode.PRODUCTS || !this.orderedProductsList) {
            return [];
        }

        return this.type === TabType.TO_PROCESS ? this.orderedProductsList.productsToProcess : this.orderedProductsList.productsProcessed;
    }

    get isTabToProcess(): boolean {
        return this.type === TabType.TO_PROCESS;
    }

    get isDisplayedByOrders(): boolean {
        return this.displayMode === DisplayMode.ORDERS;
    }

    get isDisplayedByProducts(): boolean {
        return this.displayMode === DisplayMode.PRODUCTS;
    }

    public displayedOrdersSort(o1: Order, o2: Order) {
      const sortDueDate: number = this.dueDateSort(o1, o2);

      if (sortDueDate !== 0) {
        return sortDueDate;
      }

      // if due dates are equal, sort by order number
      const sortOrderNumber: number = this.orderNumberSort(o1, o2);
      if (sortOrderNumber !== 0) {
        return sortOrderNumber;
      }

      // if order numbers are equal, sort by zone
      return this.zoneSort(o1, o2);
    }

    public dueDateSort (o1: Order, o2: Order) {
      const dueDate1: Date = new Date(o1.dueDate);
      const dueDate2: Date = new Date(o2.dueDate);
      return  dueDate1.getTime() - dueDate2.getTime();
    }

    public orderNumberSort (o1: Order, o2: Order) {
      return o1.orderNumber.localeCompare(o2.orderNumber);
    }

    public zoneSort(o1: Order, o2: Order) {
      return o1.zone.localeCompare(o2.zone);
    }
}
